import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 15000, // 15 second timeout
});

// Add auth token to requests if available
api.interceptors.request.use((config) => {
  // Use adminToken for admin routes, regular token (with admin fallback) for everything else
  const isAdminRoute = config.url?.startsWith('/admin');
  const token = isAdminRoute
    ? localStorage.getItem('adminToken')
    : (localStorage.getItem('token') || localStorage.getItem('adminToken'));
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Events
export const getEvents = () => api.get('/events');
export const getAllEvents = () => api.get('/events/all');
export const getCurrentEvent = () => api.get('/events/current');
export const getNextEvent = () => api.get('/events/next');
export const getLastCompletedEvent = () => api.get('/events/last-completed');
export const getEvent = (id) => api.get(`/events/${id}`);

// Fights
export const getFights = (eventId) => api.get(`/fights/event/${eventId}`);
export const getFight = (id) => api.get(`/fights/${id}`);
export const createFight = (data) => api.post('/fights', data);
export const updateFight = (id, data) => api.put(`/fights/${id}`, data);
export const deleteFight = (id) => api.delete(`/fights/${id}`);
export const updateFightResults = (results) => api.post('/fights/results', { results });

// User Profile
export const updateProfile = (data) => api.put('/auth/profile', data);
export const deleteAccount = () => api.delete('/auth/me');
export const checkUsername = (username) => api.get(`/auth/check-username/${username}`);
export const getMyStats = () => api.get('/auth/me/stats');
export const getUserProfile = (userId) => api.get(`/users/${userId}/profile`);

// Leagues
export const getPublicStats = () => api.get('/leagues/public-stats');
export const getMyLeagues = () => api.get('/leagues/my-leagues');
export const getLeague = (id) => api.get(`/leagues/${id}`);
export const createLeague = (data) => api.post('/leagues', data);
export const joinLeague = (inviteCode) => api.post('/leagues/join', { inviteCode });
export const getLeaguePreview = (inviteCode) => api.get(`/leagues/preview/${inviteCode}`);
export const leaveLeague = (id) => api.post(`/leagues/${id}/leave`);
export const updateLeague = (id, data) => api.put(`/leagues/${id}`, data);
export const generateNewInviteCode = (id) => api.post(`/leagues/${id}/new-invite-code`);
export const updateMemberPayment = (leagueId, userId, paid) =>
  api.put(`/leagues/${leagueId}/members/${userId}/payment`, { paid });
export const removeMember = (leagueId, userId) =>
  api.delete(`/leagues/${leagueId}/members/${userId}`);
export const deleteLeague = (id) => api.delete(`/leagues/${id}`);
export const getLeagueStandings = (id) => api.get(`/leagues/${id}/standings`);
export const getMyLeagueScore = (id) => api.get(`/picks/league/${id}/my-score`);

// Corner Chat
export const getPoolMessages = (leagueId, limit = 100) =>
  api.get(`/messages/league/${leagueId}`, { params: { limit } });
export const postPoolMessage = (leagueId, body) =>
  api.post(`/messages/league/${leagueId}`, { body });
export const getMessageUsers = (ids) =>
  api.get('/messages/users', { params: { ids: ids.join(',') } });
export const getUnreadCounts = () => api.get('/messages/unread');
export const markPoolRead = (leagueId) => api.post(`/messages/league/${leagueId}/mark-read`);
export const reportPoolMessage = (messageId, reason, details = '') =>
  api.post(`/messages/${messageId}/report`, { reason, details });
export const getLeaguePickStatus = (id, eventId) => api.get(`/leagues/${id}/picks-status`, {
  params: { event_id: eventId }
});

// Picks (league context)
export const submitPicks = (data) => api.post('/picks', data);
export const getMyPicks = (leagueId, eventId) => api.get(`/picks/league/${leagueId}/event/${eventId}`);
export const getLeaguePicks = (leagueId, eventId) => api.get(`/picks/league/${leagueId}/event/${eventId}/all`);
export const getLeagueLeaderboard = (leagueId, eventId) => api.get(`/picks/league/${leagueId}/event/${eventId}/stats`);
export const calculateLeagueScores = (leagueId, eventId) => api.post(`/picks/league/${leagueId}/event/${eventId}/calculate`);
export const getCommunityStats = (eventId) => api.get(`/picks/event/${eventId}/community-stats`);
export const getLiveLeaderboard = () => api.get('/picks/live');
export const getAllTimeStandings = (leagueId) => api.get(`/picks/league/${leagueId}/standings/all-time`);
export const getEventStandings = (leagueId, eventId) => api.get(`/picks/league/${leagueId}/standings/event/${eventId}`);
export const getCompletedEventsWithWinners = (leagueId) => api.get(`/picks/league/${leagueId}/completed-events`);

// Global Pool (PNP Title Shot)
export const getGlobalPool = () => api.get('/leagues/global');
export const enterTitleShot = () => api.post('/leagues/global/enter');
export const getUserBadges = (userId) => api.get(`/leagues/badges/${userId}`);

// Admin
export const adminLogin = (username, password) => api.post('/admin/login', { username, password });
export const adminVerify2FA = (pendingToken, totpCode) => api.post('/admin/verify-2fa', { pendingToken, totpCode });
export const adminLogout = () => api.post('/admin/logout');
export const checkAdminAuth = () => api.get('/admin/check');
export const getAdminDashboard = () => api.get('/admin/dashboard');
export const adminCreateEvent = (data) => api.post('/admin/events', data);
export const adminUpdateEventStatus = (id, status) => api.put(`/admin/events/${id}/status`, { status });
export const adminUpdateEventDate = (id, date) => api.put(`/admin/events/${id}/date`, { date });
export const adminListDuplicateEvents = () => api.get('/admin/duplicate-events');
export const adminMergeDuplicateEvents = (keepEventId, dropEventId) =>
  api.post('/admin/merge-duplicate-events', { keepEventId, dropEventId });
export const adminCreateFight = (data) => api.post('/admin/fights', data);
export const adminUpdateFightResult = (id, winner, method) => api.put(`/admin/fights/${id}/result`, { winner, method });
export const adminUpdateFight = (id, data) => api.put(`/admin/fights/${id}`, data);
export const adminDeleteEvent = (id) => api.delete(`/admin/events/${id}`);
export const adminDeleteFight = (id) => api.delete(`/admin/fights/${id}`);
// No eventUrl = scrape ufc.com and import every missing upcoming event, which
// fetches an event page, the UFC API, odds and per-fighter headshots for ~25
// events. That runs for minutes, so the default 15s timeout would abort the
// request client-side while the server happily kept importing — surfacing a
// bogus "Failed to import" while the work actually succeeded.
export const adminImportEvent = (data) =>
  api.post('/admin/events/import', data, { timeout: data?.eventUrl ? 120000 : 600000 });
export const adminRefreshEvent = (id) => api.post(`/admin/events/${id}/refresh`, {}, { timeout: 60000 });
export const adminUpdateSourceUrl = (id, sourceUrl) => api.put(`/admin/events/${id}/source-url`, { sourceUrl });
export const adminUpdateUfcStatsUrl = (id, ufcStatsUrl) => api.put(`/admin/events/${id}/ufc-stats-url`, { ufcStatsUrl });
export const adminAutoDetectStatsUrl = (id) => api.post(`/admin/events/${id}/auto-detect-stats-url`);
export const adminScrapeResults = (id) => api.post(`/admin/events/${id}/scrape-results`, {}, { timeout: 60000 });
export const adminGetResultsStatus = (id) => api.get(`/admin/events/${id}/results-status`);
export const getAdminPoolsData = (eventId) => api.get('/admin/data/pools' + (eventId ? `?event_id=${eventId}` : ''));
export const getAdminPool = (id) => api.get(`/admin/pools/${id}`);
export const getAdminPoolStandings = (id) => api.get(`/admin/pools/${id}/standings`);
export const getAdminLeaderboards = () => api.get('/admin/leaderboards');
export const adminDeletePool = (id) => api.delete(`/admin/pools/${id}`);
export const adminDeleteUser = (id) => api.delete(`/admin/users/${id}`);
export const adminRepairPicks = (id) => api.post(`/admin/events/${id}/repair-picks`);
export const adminFixEarlyPrelims = (id, count = 3) => api.post(`/admin/events/${id}/fix-early-prelims`, { count });
export const adminFetchOdds = (id) => api.post(`/admin/events/${id}/fetch-odds`);

// Admin Push Notifications
export const adminSendNotification = (data) => api.post('/admin/notifications/send', data);
export const adminGetNotificationLogs = () => api.get('/admin/notifications/logs');
export const adminGetScheduledNotifications = () => api.get('/admin/notifications/scheduled');
export const adminCreateScheduledNotification = (data) => api.post('/admin/notifications/scheduled', data);
export const adminUpdateScheduledNotification = (id, data) => api.put(`/admin/notifications/scheduled/${id}`, data);
export const adminDeleteScheduledNotification = (id) => api.delete(`/admin/notifications/scheduled/${id}`);

// Admin 2FA
export const adminSetup2FA = () => api.post('/admin/2fa/setup');
export const adminVerify2FASetup = (totpCode) => api.post('/admin/2fa/verify', { totpCode });
export const adminDisable2FA = (password, totpCode) => api.post('/admin/2fa/disable', { password, totpCode });

// Admin Activity Logs
export const getAdminActivityLogs = (params = {}) => api.get('/admin/activity', { params });

// Admin Chat Review (read-only)
export const getAdminChatPools = () => api.get('/admin/chat/pools');
export const getAdminChatMessages = (leagueId, params = {}) =>
  api.get(`/admin/chat/pools/${leagueId}/messages`, { params });

export default api;
